/**
 * Uma mensagem da conversa. Mídia carrega de /api/midias (com controle de
 * acesso no servidor). Ações por mensagem: responder, chamar alguém da
 * equipe sobre ela e copiar o texto.
 */
import type { MensagemDto } from "@ydchat/shared";
import { veOperacaoInteira } from "@ydchat/shared";
import {
  AlertCircle,
  Ban,
  Check,
  CheckCheck,
  ChevronDown,
  Clock,
  Copy,
  Download,
  FileText,
  Info,
  LayoutTemplate,
  Loader2,
  MapPin,
  PackageCheck,
  Reply,
  Star,
  StickyNote,
  UserPlus,
} from "lucide-react";
import { toast } from "sonner";
import { ItemMenu, Popover } from "@/components/ui/popover";
import { useAvaliarResposta } from "@/lib/copiloto";
import { useUsuario } from "@/lib/sessao";
import { formatarTamanho } from "@/lib/central";
import { cn } from "@/lib/cn";
import { formatarHora } from "@/lib/formatar";

export interface AcoesMensagem {
  onResponder: (m: MensagemDto) => void;
  onEncaminhar: (m: MensagemDto) => void;
}

export function Balao({ mensagem: m, acoes }: { mensagem: MensagemDto; acoes: AcoesMensagem }) {
  const saida = m.direcao === "saida";
  const interna = m.direcao === "interna";
  const daEquipe = saida || interna;
  const sistema = m.autor === "sistema";

  // Registro automático (pedido criado, status do pedido...): faixa no meio.
  if (m.tipo === "sistema") {
    return (
      <div className="my-1.5 flex justify-center" role="note">
        <span className="flex max-w-[90%] items-center gap-1.5 rounded-full bg-gold-50 px-3 py-1 text-center text-[12px] font-semibold text-gold-800 ring-1 ring-gold-200">
          {m.conteudo["pedido"] ? (
            <PackageCheck size={13} strokeWidth={2} />
          ) : (
            <Info size={13} strokeWidth={2} />
          )}
          {m.texto}
          <span className="whitespace-nowrap font-normal text-gold-700">
            · {formatarHora(m.criadoEm)}
          </span>
        </span>
      </div>
    );
  }

  return (
    <div className={cn("group flex", daEquipe ? "justify-end" : "justify-start")}>
      <div className={cn("relative max-w-[85%] md:max-w-[70%]", m.reacoes.length && "mb-3")}>
        <div
          className={cn(
            "rounded-xl px-3 py-2 text-[14.5px] leading-snug shadow-xs",
            interna && "border border-dashed border-warning-500/50 bg-warning-100 text-neutral-800",
            saida && !sistema && "bg-gold-100 text-neutral-900",
            saida && sistema && "bg-neutral-100 text-neutral-700",
            !daEquipe && "bg-white text-neutral-900",
            m.status === "falhou" && "ring-1 ring-danger-500",
          )}
        >
          <Cabecalho m={m} />
          {m.respostaA && (
            <div className="mb-1.5 rounded-md border-l-[3px] border-gold-400 bg-black/[0.04] px-2 py-1 text-[12.5px] text-neutral-600">
              <span className="font-semibold">
                {m.respostaA.direcao === "entrada" ? "Cliente" : "Equipe"}
              </span>
              <p className="line-clamp-2">{m.respostaA.texto || rotuloTipo(m.respostaA.tipo)}</p>
            </div>
          )}
          <Corpo m={m} />
          <p className="mt-0.5 flex items-center justify-end gap-1 text-[11px] text-neutral-500">
            {formatarHora(m.criadoEm)}
            {saida && <StatusEntrega status={m.status} />}
          </p>
          {m.status === "falhou" && m.erro && (
            <p className="mt-1 text-[12px] text-danger-500">{m.erro}</p>
          )}
        </div>

        {!!m.reacoes.length && (
          <span
            className={cn(
              "absolute -bottom-3 rounded-full bg-white px-1.5 text-sm shadow-sm ring-1 ring-neutral-200",
              daEquipe ? "right-2" : "left-2",
            )}
          >
            {m.reacoes.join(" ")}
          </span>
        )}

        <div
          className={cn(
            "absolute top-1 opacity-0 transition-opacity focus-within:opacity-100 group-hover:opacity-100 max-md:opacity-100",
            daEquipe ? "-left-8" : "-right-8",
          )}
        >
          <Popover
            alinhamento={daEquipe ? "inicio" : "fim"}
            largura="w-56"
            gatilho={({ alternar }) => (
              <button
                onClick={alternar}
                aria-label="Ações da mensagem"
                className="flex size-7 items-center justify-center rounded-full text-neutral-400 hover:bg-white hover:text-neutral-700 hover:shadow-xs"
              >
                <ChevronDown size={16} strokeWidth={1.75} />
              </button>
            )}
          >
            {(fechar) => (
              <>
                {!interna && (
                  <ItemMenu
                    icone={Reply}
                    onClick={() => {
                      acoes.onResponder(m);
                      fechar();
                    }}
                  >
                    Responder
                  </ItemMenu>
                )}
                <ItemMenu
                  icone={UserPlus}
                  descricao="Nota interna para alguém da equipe"
                  onClick={() => {
                    acoes.onEncaminhar(m);
                    fechar();
                  }}
                >
                  Chamar alguém
                </ItemMenu>
                {m.texto && (
                  <ItemMenu
                    icone={Copy}
                    onClick={() => {
                      void navigator.clipboard
                        .writeText(m.texto ?? "")
                        .then(() => toast.success("Texto copiado!"));
                      fechar();
                    }}
                  >
                    Copiar texto
                  </ItemMenu>
                )}
                {saida && m.autor === "usuario" && m.texto && (
                  <AvaliarResposta mensagemId={m.id} onFeito={fechar} />
                )}
              </>
            )}
          </Popover>
        </div>
      </div>
    </div>
  );
}

function Cabecalho({ m }: { m: MensagemDto }) {
  if (m.direcao === "interna") {
    return (
      <p className="mb-0.5 flex items-center gap-1 text-[11px] font-bold text-warning-500">
        <StickyNote size={12} strokeWidth={2} /> Nota interna · {m.autorUsuario?.nome}
      </p>
    );
  }
  if (m.direcao === "saida") {
    const quem =
      m.autor === "sistema"
        ? "Mensagem automática"
        : m.autor === "ia"
          ? "Assistente virtual"
          : m.autorUsuario?.nome;
    return (
      <p className="mb-0.5 flex items-center gap-1 text-[11px] font-bold text-gold-700">
        {m.tipo === "template" && <LayoutTemplate size={12} strokeWidth={2} />}
        {quem}
        {m.tipo === "template" && <span className="font-normal text-neutral-500">· modelo</span>}
      </p>
    );
  }
  return null;
}

function Corpo({ m }: { m: MensagemDto }) {
  const url = m.midia ? `/api/midias/${m.midia.id}` : null;
  if (m.midia?.removida) {
    return (
      <p className="text-[13px] italic text-neutral-500">
        {rotuloTipo(m.tipo)} removido (política de dados)
        {m.texto ? <span className="mt-1 block not-italic text-neutral-800">{m.texto}</span> : null}
      </p>
    );
  }
  const carregando = m.midia && !m.midia.disponivel;

  if (carregando) {
    return (
      <p className="flex items-center gap-2 text-[13px] text-neutral-500">
        <Loader2 size={14} className="animate-spin" /> Recebendo {rotuloTipo(m.tipo).toLowerCase()}…
      </p>
    );
  }

  switch (m.tipo) {
    case "imagem":
    case "figurinha":
      return (
        <>
          {url && (
            <a href={url} target="_blank" rel="noreferrer" className="-mx-1 -mt-0.5 mb-1 block">
              <img
                src={url}
                alt={m.texto ?? "Imagem enviada"}
                loading="lazy"
                className={cn(
                  "rounded-lg object-cover",
                  m.tipo === "figurinha"
                    ? "size-32 bg-transparent"
                    : "max-h-80 w-full max-w-xs bg-neutral-100",
                )}
              />
            </a>
          )}
          {m.texto && <Texto t={m.texto} />}
        </>
      );
    case "audio":
      return url ? (
        <audio
          controls
          preload="none"
          src={url}
          className="h-10 w-64 max-w-full"
          aria-label="Áudio"
        />
      ) : null;
    case "video":
      return (
        <>
          {url && (
            <video
              controls
              preload="metadata"
              src={url}
              className="mb-1 max-h-80 w-full max-w-xs rounded-lg bg-black"
            />
          )}
          {m.texto && <Texto t={m.texto} />}
        </>
      );
    case "documento":
      return (
        <>
          {url && m.midia && (
            <a
              href={`${url}?baixar=1`}
              className="mb-1 flex items-center gap-3 rounded-lg bg-black/[0.04] p-2.5 text-neutral-800 hover:bg-black/[0.07] hover:no-underline"
            >
              <FileText size={28} strokeWidth={1.5} className="shrink-0 text-gold-700" />
              <span className="min-w-0 flex-1">
                <span className="block truncate text-[13px] font-semibold">
                  {m.midia.nomeArquivo ?? "Documento"}
                </span>
                <span className="text-[11px] text-neutral-500">
                  {formatarTamanho(m.midia.tamanhoBytes)}
                </span>
              </span>
              <Download size={16} strokeWidth={1.75} className="shrink-0 text-neutral-500" />
            </a>
          )}
          {m.texto && <Texto t={m.texto} />}
        </>
      );
    case "localizacao": {
      const lat = m.conteudo["latitude"];
      const lng = m.conteudo["longitude"];
      return (
        <a
          href={`https://www.google.com/maps?q=${lat},${lng}`}
          target="_blank"
          rel="noreferrer"
          className="flex items-center gap-2 text-[13.5px] font-semibold"
        >
          <MapPin size={16} strokeWidth={1.75} /> {m.texto || "Localização enviada"}
        </a>
      );
    }
    case "interativo": {
      // Menu enviado (botões ou lista): mostra as opções como a cliente vê.
      const c = m.conteudo as {
        botoes?: { botoes: { id: string; titulo: string }[] };
        lista?: { botao: string; opcoes: { id: string; titulo: string }[] };
      };
      const opcoes = c.botoes?.botoes ?? c.lista?.opcoes;
      if (!opcoes) return m.texto ? <Texto t={m.texto} /> : null;
      return (
        <div>
          {m.texto && <Texto t={m.texto} />}
          {c.lista && (
            <p className="mt-1 text-[12px] font-semibold text-gold-700">{c.lista.botao}</p>
          )}
          <ul className="mt-1.5 space-y-1">
            {opcoes.map((o) => (
              <li
                key={o.id}
                className="rounded-md border border-gold-300/70 bg-white/70 px-2.5 py-1 text-center text-[13px] font-semibold text-gold-700"
              >
                {o.titulo}
              </li>
            ))}
          </ul>
        </div>
      );
    }
    case "contato":
      return <p className="text-[13px] italic text-neutral-600">Cartão de contato</p>;
    case "desconhecido":
      return (
        <p className="text-[13px] italic text-neutral-500">
          Este tipo de mensagem ainda não aparece aqui. Veja no celular.
        </p>
      );
    default:
      return m.texto ? <Texto t={m.texto} /> : null;
  }
}

function Texto({ t }: { t: string }) {
  return <p className="whitespace-pre-wrap break-words">{formatarWhatsapp(t)}</p>;
}

export function rotuloTipo(tipo: MensagemDto["tipo"]): string {
  const r: Partial<Record<MensagemDto["tipo"], string>> = {
    imagem: "Imagem",
    audio: "Áudio",
    video: "Vídeo",
    documento: "Documento",
    localizacao: "Localização",
    figurinha: "Figurinha",
    template: "Modelo",
  };
  return r[tipo] ?? "Mensagem";
}

function StatusEntrega({ status }: { status: MensagemDto["status"] }) {
  const props = { size: 14, strokeWidth: 2 };
  switch (status) {
    case "pendente":
      return <Clock {...props} aria-label="Enviando" />;
    case "enviada":
      return <Check {...props} aria-label="Enviada" />;
    case "entregue":
      return <CheckCheck {...props} aria-label="Entregue" />;
    case "lida":
      return <CheckCheck {...props} className="text-gold-600" aria-label="Lida" />;
    case "falhou":
      return <AlertCircle {...props} className="text-danger-500" aria-label="Falhou" />;
    default:
      return null;
  }
}

/** Gestão: resposta ótima vira exemplo para as sugestões da IA. */
function AvaliarResposta({ mensagemId, onFeito }: { mensagemId: string; onFeito: () => void }) {
  const usuario = useUsuario();
  const avaliar = useAvaliarResposta();
  if (!veOperacaoInteira(usuario.papel)) return null;
  return (
    <>
      <ItemMenu
        icone={Star}
        descricao="Vira exemplo para as sugestões da IA"
        onClick={() => {
          avaliar.mutate({ mensagemId, avaliacao: "otima" });
          onFeito();
        }}
      >
        Ótima resposta
      </ItemMenu>
      <ItemMenu
        icone={Ban}
        descricao="A IA não usa como exemplo"
        onClick={() => {
          avaliar.mutate({ mensagemId, avaliacao: "nao_usar" });
          onFeito();
        }}
      >
        Não usar como exemplo
      </ItemMenu>
    </>
  );
}

/** Formatação do WhatsApp (*negrito*, _itálico_, ~riscado~) e links clicáveis. */
function formatarWhatsapp(t: string): React.ReactNode[] {
  const nos: React.ReactNode[] = [];
  let k = 0;
  for (const parte of t.split(/(https?:\/\/[^\s]+)/g)) {
    if (/^https?:\/\//.test(parte)) {
      nos.push(
        <a
          key={k++}
          href={parte}
          target="_blank"
          rel="noreferrer"
          className="break-all font-semibold underline underline-offset-2"
        >
          {parte}
        </a>,
      );
      continue;
    }
    for (const trecho of parte.split(/(\*[^*\n]+\*|_[^_\n]+_|~[^~\n]+~)/g)) {
      if (/^\*[^*\n]+\*$/.test(trecho)) nos.push(<strong key={k++}>{trecho.slice(1, -1)}</strong>);
      else if (/^_[^_\n]+_$/.test(trecho)) nos.push(<em key={k++}>{trecho.slice(1, -1)}</em>);
      else if (/^~[^~\n]+~$/.test(trecho)) nos.push(<s key={k++}>{trecho.slice(1, -1)}</s>);
      else if (trecho) nos.push(trecho);
    }
  }
  return nos;
}
